/**
 * Frontend port of the EECS helpers from app/utils/eecs_utils.py.
 * Maps the AIB member code (first two digits of an EECS account number) to an
 * ISO 3166-1 alpha-2 country code, and validates the EECS account identifier format.
 */

const AIB_MEMBER_CODE_TO_COUNTRY: Record<string, string> = {
  "23": "EE",
  "08": "NO",
  "07": "NL",
  "52": "SE",
  "29": "BE",
  "32": "CH",
  "42": "ES",
  "06": "IT",
  "33": "BE",
  "22": "CZ",
  "41": "FI",
  "12": "AT",
  "43": "BE",
  "34": "BE",
  "21": "CY",
  "38": "DE",
  "02": "DK",
  "35": "FR",
  "40": "HR",
  "39": "IE",
  "37": "IS",
  "36": "LU",
  "17": "SI",
  "27": "LT",
  "20": "SK",
  "19": "PT",
  "47": "RS",
  "25": "LV",
  "28": "GR",
  "24": "HU",
};

// Belgium runs four registries, so its AIB member codes name a region rather than just the country.
const AIB_MEMBER_CODE_TO_REGISTRY_REGION: Record<string, string> = {
  "29": "BE-FLA",
  "33": "BE-WAL",
  "34": "BE-BRU",
  "43": "BE-FED",
};

// The `[` character is intentional — real EECS identifiers like Latvia's 25XAD2B7C[ include it.
const EECS_ACCOUNT_IDENTIFIER_PATTERN = /^[0-9]{2}X[0-9A-Z[]{7}$/;

export function getCountryFromEecs(
  eecs: string | null | undefined
): string | null {
  if (!eecs || eecs.length < 2) return null;
  return AIB_MEMBER_CODE_TO_COUNTRY[eecs.substring(0, 2)] ?? null;
}

/**
 * The registry an EECS account belongs to: Belgium's regional registry (BE-FLA, BE-WAL, BE-BRU,
 * BE-FED) for its four prefixes, else the country. Mirrors get_registry_region_from_eecs in
 * app/utils/eecs_utils.py.
 */
export function getRegistryRegionFromEecs(
  eecs: string | null | undefined
): string | null {
  if (!eecs || eecs.length < 2) return null;
  const prefix = eecs.substring(0, 2);
  return (
    AIB_MEMBER_CODE_TO_REGISTRY_REGION[prefix] ??
    AIB_MEMBER_CODE_TO_COUNTRY[prefix] ??
    null
  );
}

export function isEecsAccountIdentifier(
  eecs: string | null | undefined
): boolean {
  if (!eecs) return false;
  return EECS_ACCOUNT_IDENTIFIER_PATTERN.test(eecs.toUpperCase());
}

// Ofgem (UK) RER registry accounts do not use the AIB numeric scheme. Account identifiers
// are a known prefix followed by 7 digits: GEN (generator), PAR (participant), SUP (supplier),
// AGE (agent), e.g. GEN0221665, PAR6571233, SUP0009523, AGE0000432. Add prefixes here
// as Ofgem exposes more account types.
const OFGEM_ACCOUNT_PREFIXES = ["GEN", "PAR", "SUP", "AGE"] as const;
const OFGEM_ACCOUNT_IDENTIFIER_PATTERN = new RegExp(
  `^(${OFGEM_ACCOUNT_PREFIXES.join("|")})[0-9]{7}$`
);

export function isOfgemAccountIdentifier(
  value: string | null | undefined
): boolean {
  if (!value) return false;
  return OFGEM_ACCOUNT_IDENTIFIER_PATTERN.test(value.toUpperCase());
}

/**
 * Whether the value matches a known registry account format (EECS or Ofgem).
 * Distinct from country derivability: an EECS-formatted value with an unknown
 * AIB prefix (e.g. 99X1234567) is recognized here but derives no country.
 * Mirrors the strict branch of app/money/admin/transaction_validator.py — the
 * backend accepts recognized values without the relaxed_account_validation flag.
 */
export function isRecognizedRegistryAccount(
  value: string | null | undefined
): boolean {
  return isEecsAccountIdentifier(value) || isOfgemAccountIdentifier(value);
}

// Registry account numbers are letters and digits only; recognized formats
// (EECS, Ofgem) are a subset — EECS may also contain "[" (e.g. Latvia's
// 25XAD2B7C[), so recognized identifiers are accepted explicitly. Mirrors the
// backend's relaxed-branch pattern in app/money/admin/transaction_validator.py.
const ACCOUNT_NUMBER_PATTERN = /^[A-Z0-9]+$/i;

export function isAcceptableAccountNumber(value: string): boolean {
  return (
    !value ||
    ACCOUNT_NUMBER_PATTERN.test(value) ||
    isRecognizedRegistryAccount(value)
  );
}

/**
 * Registry country derived from a counterparty account number, but only when the
 * format is recognized: strict EECS -> AIB prefix country, Ofgem -> GB, else null.
 * Unlike getCountryFromEecs, a typo-shaped EECS value derives nothing.
 */
export function getCountryFromAccountNumber(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const normalized = value.toUpperCase();
  if (isEecsAccountIdentifier(normalized)) {
    return getCountryFromEecs(normalized);
  }
  if (isOfgemAccountIdentifier(normalized)) {
    return "GB";
  }
  return null;
}
